import React, { useRef, useState } from 'react';
import { Alert, Box, Button, TextField } from '@mui/material';
import { QrCodeScanner } from '@mui/icons-material';
import ScanDialog from '../../scan/ScanDialog';
import { parseScanUrl } from '../../scan/parseScanUrl';
import { ItemService } from '../../services/item.service';
import { Item } from '../../interfaces/item.interface';

export interface ScanOutcome {
  ok: boolean;
  message: string;
}

interface Props {
  /** Called with the item the code resolved to. Return what happened so the field can show it. */
  onItem: (item: Item) => ScanOutcome | Promise<ScanOutcome>;
  label?: string;
  /** No bottom margin, for use as a cell in a flex-wrap toolbar. */
  compact?: boolean;
  'data-testid'?: string;
}

/**
 * Q3: scan-to-add. A typed code (USB scanners type it and press Enter) or a camera scan of a barcode or item QR is
 * looked up in this company; the screen decides what to do with the item. Additive: the screen's own pickers stay.
 */
const ItemScanField: React.FC<Props> = ({ onItem, label = 'Scan or type item barcode', compact = false, 'data-testid': testId = 'item-scan' }) => {
  const [code, setCode] = useState('');
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<ScanOutcome | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const busyRef = useRef(false);       // a scan arriving while one is being resolved waits in this queue instead of being lost
  const queue = useRef<string[]>([]);
  const onItemRef = useRef(onItem);
  onItemRef.current = onItem;          // a queued scan must run against the screen's latest state, not the one it was typed under

  const handle = async (raw: string) => {
    const text = raw.trim();
    if (!text) return;
    if (busyRef.current) { queue.current.push(text); return; }
    busyRef.current = true;
    try {
      const target = parseScanUrl(text);
      let item: Item;
      if (target && (target.type === 'item' || target.type === 'lot')) {
        // The server resolves an item or lot id inside the selected company only, so another company's label is a plain "not found".
        item = await ItemService.lookup(target.id);
      } else if (target) {
        setResult({ ok: false, message: 'This QR code is not an item or lot label. Scan an item barcode instead.' });
        return;
      } else {
        item = await ItemService.lookup(text);
      }
      setResult(await onItemRef.current(item));
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setResult({
        ok: false,
        message: status === 404 || status === 403
          ? `No item has the code "${text}" in this company.`
          : 'Could not look up the code. Try again.',
      });
    } finally {
      busyRef.current = false;
      inputRef.current?.focus();
      if (queue.current.length > 0) setTimeout(() => { const next = queue.current.shift(); if (next) void handle(next); }, 0);
    }
  };

  return (
    <Box data-testid={testId} sx={{ mb: compact ? 0 : 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField data-help="store.scan.field.code" size="small" label={label} value={code}
          sx={{ flex: '1 1 240px', maxWidth: 360 }}
          onChange={e => setCode(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const typed = code; setCode(''); void handle(typed); } }}
          inputRef={inputRef} inputProps={{ 'aria-label': label }} />
        <Button data-help="store.scan.open" variant="outlined" startIcon={<QrCodeScanner />} onClick={() => setOpen(true)}>Scan</Button>
      </Box>
      {result && (
        <Alert severity={result.ok ? 'success' : 'warning'} sx={{ mt: 1 }} onClose={() => setResult(null)}>
          {result.message}
        </Alert>
      )}
      <ScanDialog open={open} onClose={() => setOpen(false)} title="Scan an item barcode"
        manualLabel="Or type the barcode or item code"
        onScan={text => { setOpen(false); void handle(text); }} />
    </Box>
  );
};

export default ItemScanField;
